import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Zap, X, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DISMISS_KEY = 'lf_trial_banner_dismissed_at';

export default function TrialBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    setDismissed(!!raw);
  }, []);

  if (!user || user.is_pro) return null;

  const trial = user.trial || {};
  const expired = trial.expired;
  const inTrial = trial.in_trial;

  // Expired state: always visible, cannot be dismissed
  if (expired) {
    return (
      <div
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5"
        style={{
          background: 'linear-gradient(90deg,#9a3412 0%,#b45309 100%)',
          color: '#ffffff',
        }}
      >
        <div className="flex items-center gap-2 text-sm">
          <Crown size={14} />
          <b>Your Pro trial has ended.</b>
          <span className="hidden sm:inline opacity-90">
            Subscribe to unlock GPT-5 explanations, rate chat, load board, and PDF confirmations.
          </span>
        </div>
        <button
          onClick={() => navigate('/upgrade')}
          className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg"
          style={{ background: '#ffffff', color: '#9a3412', border: 'none', cursor: 'pointer' }}
        >
          <Zap size={12} /> Upgrade $49/mo
        </button>
      </div>
    );
  }

  // In-trial state: dismissible per session
  if (inTrial && !dismissed) {
    const days = trial.days_remaining ?? 0;
    const label =
      days <= 1
        ? 'Last day of your Pro trial'
        : `${days} days left in your Pro trial`;
    return (
      <div
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5"
        style={{
          background: 'linear-gradient(90deg,#1e3a8a 0%,#2563eb 100%)',
          color: '#ffffff',
        }}
      >
        <div className="flex items-center gap-2 text-sm">
          <Clock size={14} />
          <b>{label}</b>
          <span className="hidden sm:inline opacity-90">
            Enjoying Pro features? Subscribe to keep them after the trial ends.
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate('/upgrade')}
            className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg"
            style={{ background: '#ffffff', color: '#1e3a8a', border: 'none', cursor: 'pointer' }}
          >
            <Zap size={12} /> Upgrade
          </button>
          <button
            onClick={() => {
              sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
              setDismissed(true);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#ffffff',
              opacity: 0.8,
            }}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
